import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button, Input, Badge, Empty } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Plus, Check, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Seasons() {
    const { activeClubId, setActiveSeasonId, isClubAdmin } = useAuth();
    const [seasons, setSeasons] = useState([]);
    const [campaignSlugs, setCampaignSlugs] = useState({});
    const [campaignFormVersions, setCampaignFormVersions] = useState({});
    const { open, openModal, closeModal } = useModal();
    const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_active: false });

    const load = useCallback(async () => {
        if (!activeClubId) return;
        const response = await api.get("/seasons", { params: { club_id: activeClubId } });
        setSeasons(response.data);
        setCampaignSlugs(Object.fromEntries(response.data.map((season) => [
            season.id, season.registration_slug || "",
        ])));
        setCampaignFormVersions(Object.fromEntries(response.data.map((season) => [
            season.id, season.registration_form_version || "2026-2027-v1",
        ])));
    }, [activeClubId]);
    useEffect(() => {
        load().catch((err) => toast.error(formatApiError(err)));
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/seasons", form, { params: { club_id: activeClubId } });
            toast.success("Saison créée");
            closeModal();
            setForm({ name: "", start_date: "", end_date: "", is_active: false });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const activate = async (id) => {
        await api.post(`/seasons/${id}/activate`, null, { params: { club_id: activeClubId } });
        setActiveSeasonId(id);
        toast.success("Saison activée");
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette saison ?")) return;
        await api.delete(`/seasons/${id}`, { params: { club_id: activeClubId } });
        toast.success("Saison supprimée");
        load();
    };

    const toggleRegistration = async (season) => {
        if (season.registration_open && !window.confirm(
            "Fermer les inscriptions ? Les nouvelles demandes publiques seront arrêtées, mais les dossiers existants resteront inchangés."
        )) return;
        try {
            const payload = {
                registration_open: !season.registration_open,
            };
            if (!season.registration_open) {
                payload.registration_slug = campaignSlugs[season.id];
                payload.registration_form_version = campaignFormVersions[season.id];
            }
            await api.patch(`/seasons/${season.id}/registration-campaign`, payload);
            await load();
            toast.success(season.registration_open ? "Inscriptions fermées" : "Inscriptions ouvertes");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="seasons-page">
            <PageHeader
                title="Saisons"
                subtitle="Gérez les saisons sportives du club"
                action={<Button onClick={openModal} data-testid="add-season-button"><Plus className="w-4 h-4" /> Nouvelle saison</Button>}
            />

            {seasons.length === 0 ? (
                <Empty title="Aucune saison" hint="Créez votre première saison pour commencer." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {seasons.map((s) => (
                        <Card key={s.id}>
                            <CardBody>
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-display text-2xl font-black text-slate-900">{s.name}</div>
                                        <div className="text-xs text-slate-500 mt-1">{s.start_date || "—"} → {s.end_date || "—"}</div>
                                    </div>
                                    {s.is_active && <Badge color="emerald">Active</Badge>}
                                </div>
                                <div className="flex gap-2 mt-4">
                                    {!s.is_active && (
                                        <Button variant="secondary" onClick={() => activate(s.id)} data-testid={`activate-season-${s.id}`}>
                                            <Check className="w-4 h-4" /> Activer
                                        </Button>
                                    )}
                                    <Button variant="outline" onClick={() => remove(s.id)} data-testid={`delete-season-${s.id}`}>
                                        <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                </div>
                                {isClubAdmin && (
                                    <div className="mt-5 border-t border-slate-100 pt-4 space-y-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-semibold text-slate-700">Campagne d'inscription</span>
                                            <Badge color={s.registration_open ? "emerald" : "slate"}>
                                                {s.registration_open ? "Inscriptions ouvertes" : "Inscriptions fermées"}
                                            </Badge>
                                        </div>
                                        {s.registration_slug && (
                                            <a className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline"
                                               href={`/inscription/${s.registration_slug}`} target="_blank" rel="noreferrer">
                                                /inscription/{s.registration_slug} <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                        {!s.registration_open && (
                                            <>
                                            <Input label="Slug public" value={campaignSlugs[s.id] || ""}
                                                   placeholder="ex: assmbb"
                                                   onChange={(event) => setCampaignSlugs({
                                                       ...campaignSlugs, [s.id]: event.target.value,
                                                   })}
                                                   data-testid={`registration-slug-${s.id}`} />
                                            <Input label="Version du formulaire" value={campaignFormVersions[s.id] || ""}
                                                   placeholder="ex: 2026-2027-v1"
                                                   onChange={(event) => setCampaignFormVersions({
                                                       ...campaignFormVersions, [s.id]: event.target.value,
                                                   })}
                                                   data-testid={`registration-form-version-${s.id}`} />
                                            </>
                                        )}
                                        <Button variant={s.registration_open ? "outline" : "secondary"}
                                                onClick={() => toggleRegistration(s)}
                                                disabled={!s.registration_open && (
                                                    !s.is_active || !campaignSlugs[s.id]?.trim()
                                                    || !campaignFormVersions[s.id]?.trim()
                                                )}
                                                data-testid={`toggle-registration-${s.id}`}>
                                            {s.registration_open ? "Fermer les inscriptions" : "Ouvrir les inscriptions"}
                                        </Button>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={closeModal} title="Nouvelle saison">
                <form onSubmit={submit} className="space-y-4" data-testid="season-form">
                    <Input label="Nom (ex: 2026-2027)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="season-name-input" />
                    <div className="grid grid-cols-2 gap-3">
                        <Input label="Date début" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        <Input label="Date fin" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                        Définir comme saison active
                    </label>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" type="button" onClick={closeModal}>Annuler</Button>
                        <Button type="submit" data-testid="season-submit">Créer</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
